import { BRAND } from '@/styles/brand-tokens';

/**
 * Token block for the dense staff design system.
 *
 * Consumed by BOTH `LibrarianLayout` and `AdminLayout`. The two shells render
 * different sidebars and topbars but share one visual language, so they must
 * share one token block: previously `AdminLayout` carried a hand-copied fork
 * with all 22 hex values inlined, which had already drifted (`--lib-success`
 * was `#166634` there vs `#166534` in `brand-tokens`). Import this; never
 * re-declare these variables.
 *
 * The `.librarian-app` class name is shared by both shells and is load-bearing
 * across ~100 files. It names the *design system*, not the librarian role.
 */
export const LIB_THEME_CSS = `
.librarian-app {
  --lib-bg: ${BRAND.paper};
  --lib-sidebar: ${BRAND.parchment};
  --lib-surface: ${BRAND.paper};
  --lib-surface-2: #F9F7F1;
  --lib-ink: ${BRAND.ink.light};
  --lib-ink-muted: #4A5066;
  --lib-hairline: #E8E4DC;
  --lib-field-border: ${BRAND.fieldBorder.light};
  --lib-navy: ${BRAND.navy.light};
  --lib-gold: ${BRAND.gold.light};
  --lib-gold-text: ${BRAND.goldText.light};
  --lib-danger: ${BRAND.status.danger.light};
  --lib-success: ${BRAND.status.success.light};
  color: var(--lib-ink);
  background: var(--lib-bg);
}
.dark .librarian-app {
  --lib-bg: ${BRAND.deepNavy};
  --lib-sidebar: #0F1832;
  --lib-surface: #0F1832;
  --lib-surface-2: #14203E;
  --lib-ink: ${BRAND.ink.dark};
  --lib-ink-muted: #A8ADBB;
  --lib-hairline: ${BRAND.navy.light};
  --lib-field-border: ${BRAND.fieldBorder.dark};
  --lib-navy: ${BRAND.gold.light};
  --lib-gold: ${BRAND.gold.dark};
  --lib-gold-text: ${BRAND.goldText.dark};
  --lib-danger: ${BRAND.status.danger.dark};
  --lib-success: ${BRAND.status.success.dark};
}

/*
 * Foreground for gold fills. Declared OUTSIDE the theme blocks on purpose:
 * gold is light in both themes, so this must not flip. Keeping it here makes
 * it structurally impossible for a future dark-mode edit to invert it.
 */
.librarian-app,
.dark .librarian-app {
  --lib-on-gold: ${BRAND.onGold};
}

/* Keyboard focus. The staff app is used keyboard-first all day and previously
   relied on browser defaults while the public shell had a deliberate ring. */
.librarian-app :focus-visible {
  outline: 2px solid var(--lib-gold);
  outline-offset: 2px;
  border-radius: 4px;
}

.librarian-app *::-webkit-scrollbar { width: 8px; height: 8px; }
.librarian-app *::-webkit-scrollbar-thumb {
  background: var(--lib-hairline); border-radius: 4px;
}

@media (prefers-reduced-motion: reduce) {
  .librarian-app *, .librarian-app *::before, .librarian-app *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}
`;
